import { useEffect, useMemo, useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { UserBadge } from '@/components/UserBadge'
import {
  Inbox,
  Send,
  Cog,
  Workflow,
  Bot,
  Star,
  Webhook,
  Users,
  Filter,
  ClipboardList,
  Link2,
  Compass,
  HandHelping,
  Eye,
  LayoutDashboard,
  Shield,
  Scale,
  Megaphone,
  Briefcase,
  RefreshCw,
  ShieldCheck,
  MessageSquareOff,
  BookOpen,
  ChevronDown,
  type LucideIcon,
} from 'lucide-react'
import { api } from '@/lib/api'

type NavItem = {
  to: string
  label: string
  icon: LucideIcon
  /** Muestra un contador de pendientes (handoffs abiertos / DSR urgentes). */
  badge?: 'handoffs' | 'rgpd'
  /** Solo tiene sentido con canales en modo mock (marca "mock"). */
  mockOnly?: boolean
  /** Enlace externo real (pestaña nueva), no ruta de react-router. */
  external?: boolean
  /** Aún no operativo. */
  disabled?: boolean
}
type NavGroup = { label: string; items: NavItem[] }

// Navegación agrupada por menú/submenú. El orden va de lo más usado (arriba) a
// la configuración (abajo). Los grupos son contenedores donde ir metiendo features.
const navGroups: NavGroup[] = [
  {
    label: 'Principal',
    items: [
      { to: '/marketing/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { to: '/marketing/inbox', label: 'Inbox', icon: Inbox },
      { to: '/marketing/clients', label: 'Clientes', icon: Users },
    ],
  },
  {
    label: 'Marketing',
    items: [
      { to: '/marketing/campaigns', label: 'Campañas', icon: Megaphone },
      { to: '/marketing/smart-lists', label: 'SmartLists', icon: Filter },
      { to: '/marketing/pipeline', label: 'Pipeline', icon: Briefcase },
      { to: '/marketing/reputation', label: 'Reputación', icon: Star },
    ],
  },
  {
    label: 'Captación',
    items: [
      { to: '/marketing/forms', label: 'Formularios', icon: ClipboardList },
      { to: '/marketing/trigger-links', label: 'Trigger Links', icon: Link2 },
      { to: '/marketing/routing-rules', label: 'Routing', icon: Compass },
    ],
  },
  {
    label: 'IA & Automatización',
    items: [
      { to: '/marketing/bots', label: 'Bots', icon: Bot },
      { to: '/marketing/handoffs', label: 'Handoffs', icon: HandHelping, badge: 'handoffs' },
      { to: '/marketing/shadow-review', label: 'Shadow review', icon: Eye },
      { to: '/marketing/workflows', label: 'Workflows', icon: Workflow },
    ],
  },
  {
    label: 'Cumplimiento',
    items: [
      { to: '/marketing/rgpd', label: 'RGPD', icon: Scale, badge: 'rgpd' },
      { to: '/marketing/sub-processors', label: 'Sub-procesadores', icon: Shield },
      { to: '/marketing/opt-out-keywords', label: 'Opt-out STOP', icon: MessageSquareOff },
    ],
  },
  {
    label: 'Configuración',
    items: [
      { to: '/marketing/sync', label: 'Sincronización', icon: RefreshCw },
      { to: '/marketing/webhooks', label: 'Webhooks', icon: Webhook },
      { to: '/marketing/security', label: 'Seguridad', icon: ShieldCheck },
      { to: '/marketing/simulator', label: 'Simulador', icon: Send, mockOnly: true },
      { to: '/manual.html', label: 'Ayuda / Manual', icon: BookOpen, external: true },
      { to: '/marketing/settings', label: 'Ajustes', icon: Cog, disabled: true },
    ],
  },
]

const COLLAPSE_KEY = 'mkt.nav.collapsed'

function loadCollapsed(): Set<string> {
  try {
    const raw = localStorage.getItem(COLLAPSE_KEY)
    return new Set<string>(raw ? (JSON.parse(raw) as string[]) : [])
  } catch {
    return new Set<string>()
  }
}

export function AppShell() {
  const location = useLocation()
  const [handoffsOpen, setHandoffsOpen] = useState<number | null>(null)
  const [rgpdUrgent, setRgpdUrgent] = useState<number | null>(null)
  const [collapsed, setCollapsed] = useState<Set<string>>(loadCollapsed)

  function persist(next: Set<string>) {
    try {
      localStorage.setItem(COLLAPSE_KEY, JSON.stringify([...next]))
    } catch {
      // localStorage no disponible — el estado sigue en memoria.
    }
  }

  function toggleGroup(label: string) {
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(label)) next.delete(label)
      else next.add(label)
      persist(next)
      return next
    })
  }

  // Grupo que contiene la ruta activa: se auto-expande al navegar a él.
  const activeGroup = useMemo(
    () =>
      navGroups.find((g) =>
        g.items.some((it) => !it.external && location.pathname.startsWith(it.to)),
      )?.label,
    [location.pathname],
  )

  useEffect(() => {
    if (!activeGroup) return
    setCollapsed((prev) => {
      if (!prev.has(activeGroup)) return prev
      const next = new Set(prev)
      next.delete(activeGroup)
      persist(next)
      return next
    })
  }, [activeGroup])

  useEffect(() => {
    let cancelled = false
    async function poll() {
      try {
        const [h, r] = await Promise.all([
          api<{ open: number }>('/v1/marketing/bots/handoffs/count').catch(() => ({ open: 0 })),
          api<{ urgent: number; pending: number }>(
            '/v1/marketing/rgpd/dsr?status=PENDING&deadlineDays=7',
          )
            .then((x) => ({ urgent: (x as { items?: unknown[] }).items?.length ?? 0, pending: 0 }))
            .catch(() => ({ urgent: 0, pending: 0 })),
        ])
        if (!cancelled) {
          setHandoffsOpen(h.open ?? null)
          setRgpdUrgent(r.urgent ?? null)
        }
      } catch {
        // silent
      }
    }
    void poll()
    const t = setInterval(() => void poll(), 15_000)
    return () => {
      cancelled = true
      clearInterval(t)
    }
  }, [])

  function itemBadge(item: NavItem): number | null {
    if (item.badge === 'handoffs' && handoffsOpen && handoffsOpen > 0) return handoffsOpen
    if (item.badge === 'rgpd' && rgpdUrgent && rgpdUrgent > 0) return rgpdUrgent
    return null
  }

  function renderItem(item: NavItem) {
    const { to, label, icon: Icon } = item
    const badgeCount = itemBadge(item)
    if (item.external) {
      return (
        <a
          key={to}
          href={to}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-3 py-1.5 text-sm rounded text-ink-700 hover:bg-surface-alt"
        >
          <Icon className="size-4" />
          <span>{label}</span>
        </a>
      )
    }
    return (
      <NavLink
        key={to}
        to={to}
        aria-disabled={item.disabled}
        onClick={(e) => item.disabled && e.preventDefault()}
        className={({ isActive }) =>
          [
            'flex items-center gap-2 px-3 py-1.5 text-sm rounded',
            item.disabled
              ? 'text-ink-700/40 cursor-not-allowed'
              : isActive
                ? 'bg-blueprint-50 text-blueprint-700 font-medium'
                : 'text-ink-700 hover:bg-surface-alt',
          ].join(' ')
        }
      >
        <Icon className="size-4" />
        <span>{label}</span>
        {badgeCount != null && (
          <span className="ml-auto bp-pill bg-red-600 text-white">{badgeCount}</span>
        )}
        {item.mockOnly && badgeCount == null && (
          <span className="ml-auto bp-pill" title="Solo en modo mock">
            mock
          </span>
        )}
      </NavLink>
    )
  }

  return (
    <div className="flex h-full">
      <aside className="w-56 shrink-0 border-r border-ink-900/10 bg-white flex flex-col">
        <div className="px-4 py-4 border-b border-ink-900/10">
          <img src="/logo-jr2mkt.svg" alt="JR2 MKT" className="h-6 w-auto" />
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-700/60 mt-1.5">
            add-on
          </div>
          <div className="mt-3">
            <UserBadge />
          </div>
        </div>
        <nav className="flex-1 px-2 py-3 overflow-y-auto">
          {navGroups.map((group) => {
            const isOpen = !collapsed.has(group.label)
            // Suma de pendientes del grupo — para verlos aunque esté colapsado.
            const groupBadge = group.items.reduce((n, it) => n + (itemBadge(it) ?? 0), 0)
            return (
              <div key={group.label} className="mb-1.5">
                <button
                  type="button"
                  onClick={() => toggleGroup(group.label)}
                  aria-expanded={isOpen}
                  className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded text-[10px] font-mono uppercase tracking-widest text-ink-700/50 hover:text-ink-900 hover:bg-surface-alt"
                >
                  <ChevronDown
                    className={['size-3 shrink-0 transition-transform', isOpen ? '' : '-rotate-90'].join(
                      ' ',
                    )}
                    aria-hidden="true"
                  />
                  <span>{group.label}</span>
                  {!isOpen && groupBadge > 0 && (
                    <span className="ml-auto bp-pill bg-red-600 text-white">{groupBadge}</span>
                  )}
                </button>
                {isOpen && <div className="space-y-0.5 mt-0.5">{group.items.map(renderItem)}</div>}
              </div>
            )
          })}
        </nav>
        <div className="px-4 py-3 border-t border-ink-900/10 font-mono text-[10px] text-ink-700/60">
          JR2 MKT · add-on
        </div>
      </aside>
      <main className="flex-1 overflow-auto bp-grid">
        <Outlet />
      </main>
    </div>
  )
}
